import { AddCircle } from "@mui/icons-material";
import { Button, LinearProgress } from "@mui/material";
import { makeStyles } from "@mui/styles";
import LinkIconButton from "components/base/LinkIconButton";
import ErrorBox from "components/error/ErrorBox";
import { UserNameCellRenderer } from "components/grid/CellRenderers";
import DataGrid, { Column } from "devextreme-react/data-grid";
import { ActionBarContent } from "features/shell";
import { RestrictedTenantRealm, TenantPermission } from "features/tenant";
import React from "react";
import { useHistory } from "react-router-dom";
import { createRoute } from "utils/url";
import "../../utils/custom-dx-theme/dx.material.custom-scheme.css";
import type { AccessGrants_grants_Grants_allGrants_GrantWithoutToken as AccessGrantRow } from "./schema/AccessGrants";
import { useAccessGrants } from "./useAccessGrants";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    height: "100%",
    overflow: "hidden",
    marginBottom: theme.spacing(2)
  },
  dataGrid: {
    "&.dx-widget .dx-datagrid .dx-datagrid-header-panel": {
      backgroundColor: "#F5F7F7"
    },
    "&.dx-widget .dx-datagrid .dx-toolbar": {
      backgroundColor: "#F5F7F7"
    },
    "&.dx-widget .dx-datagrid .dx-datagrid-headers .dx-datagrid-table .dx-row > td":
      {
        backgroundColor: "#E6E6E6",
        fontWeight: 600,
        fontSize: 12,
        color: "#1E1E1E",
        padding: 0,
        paddingLeft: theme.spacing(1),
        height: 24,
        borderRight: "solid 1px #aaa",
        borderLeft: "solid 1px #aaa",
        gap: 4,
        "&:hover": { color: "red" }
      },
    "&.dx-widget .dx-datagrid .dx-datagrid-headers .dx-datagrid-table .dx-row > td:hover .dx-datagrid-text-content":
      { color: "#6e6e6e" },
    "&.dx-widget .dx-datagrid .dx-datagrid-headers .dx-datagrid-table .dx-row > td:hover:not(.dx-command-select):not(.dx-command-expand):not( .dx-editor-cell ):not(.dx-command-edit):not(.dx-datagrid-group-space)":
      {
        backgroundColor: "#E6E6E6"
      },
    "&.dx-widget .dx-datagrid .dx-row-lines > td": {
      border: "none",
      padding: "4px 8px",
      height: 40,
      color: "#464646",
      fontSize: 12,
      fontWeight: 400,
      textAlign: "left !important"
    },
    "&.dx-widget .dx-datagrid .dx-datagrid-columns-separator": {
      backgroundColor: "#464646",
      width: 1
    },
    "&.dx-widget .dx-datagrid .dx-datagrid-columns-separator-transparent": {
      backgroundColor: "transparent"
    },
    "&.dx-widget .dx-datagrid .dx-datagrid-rowsview .dx-row": {
      "&:hover": { backgroundColor: "#EEF6F8" }
    },
    "&.dx-widget .dx-datagrid .dx-link ": { color: "#005F87" }
  },
  viewDetailsButton: {
    color: "black",
    borderColor: "#bdbdbd",
    textTransform: "none"
  }
}));

const AccessGrants: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();

  const { data, loading, error } = useAccessGrants();

  const grants = data?.grants?.allGrants || [];

  function handleViewDetails(id: string) {
    history.push(createRoute(`/api/access-grants/grant/${id}`));
  }

  function renderActionsCell(cellData: { data: AccessGrantRow }) {
    return (
      <Button
        variant="outlined"
        size="small"
        className={classes.viewDetailsButton}
        onClick={e => {
          e.stopPropagation();
          handleViewDetails(cellData.data.id);
        }}
        data-testid={`view-grant-${cellData.data.id}`}
      >
        View Details
      </Button>
    );
  }

  return (
    <RestrictedTenantRealm
      requiredPermissions={[TenantPermission.ManageAccessGrants]}
    >
      <>
        {loading && <LinearProgress />}
        <ActionBarContent>
          <LinkIconButton
            to="/api/access-grants/create"
            title="Add Grant"
            testId="add-access-grant"
            icon={AddCircle}
          />
        </ActionBarContent>
        <div className={classes.root}>
          <DataGrid
            className={classes.dataGrid}
            dataSource={grants}
            columnAutoWidth
            showBorders={true}
            data-testid="access-grants-grid"
            height={"100%"}
          >
            <Column dataField="name" caption="Name" />
            <Column
              dataField="createdBy.email"
              caption="Creator"
              cellRender={p => {
                const creator = p.data.createdBy;
                return creator ? <UserNameCellRenderer user={creator} /> : null;
              }}
            />
            <Column
              dataField="createdAt"
              caption="Creation time"
              dataType="datetime"
              width={200}
            />
            <Column
              caption="Actions"
              width={150}
              cellRender={renderActionsCell}
              allowSorting={false}
            />
          </DataGrid>
        </div>
        {error ? <ErrorBox apolloError={error} closable /> : null}
      </>
    </RestrictedTenantRealm>
  );
};

export default AccessGrants;
